import { memo, useEffect, useRef, useState, ReactNode } from "react";
import type { Swiper as SwiperClass } from "swiper";
import type { SwiperModule } from "swiper/types";
import createStoriesSlider, {
  type StoriesSliderControls,
} from "./stories-slider.esm.js";
import StorySpinner from "./StorySpinner.js";

interface StoriesSliderProps {
  swiper: typeof SwiperClass;
  effectCube: SwiperModule;
  enabled?: boolean;
  autoplayDuration?: number;
  onSlidesIndexesChange?: (subSwiperIndex: number, activeIndex: number) => void;
  allowTouchMove?: boolean;
  onStoriesSlider?: (instance: StoriesSliderControls) => void;
  onAutoplayStart?: (swiper: SwiperClass) => void;
  onAutoplayStop?: (swiper: SwiperClass) => void;
  children?: ReactNode;
  closeModal?: () => void;
  storiesSeen?: (index: number) => void;
}

const StoriesSlider = memo(
  ({
    swiper,
    effectCube,
    autoplayDuration = 5000,
    onSlidesIndexesChange,
    allowTouchMove = true,
    onStoriesSlider,
    onAutoplayStart,
    onAutoplayStop,
    children,
    closeModal,
    storiesSeen,
  }: StoriesSliderProps) => {
    const elRef = useRef<HTMLDivElement>(null);
    const storiesSliderRef = useRef<StoriesSliderControls | null>(null);
    const [mediaLoaded, setMediaLoaded] = useState(true);
    const mediaCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
      null
    );

    // Robust media loading check
    const checkMediaLoaded = useRef(() => {
      if (mediaCheckTimeoutRef.current) {
        clearTimeout(mediaCheckTimeoutRef.current);
      }
      setMediaLoaded(false);

      const visibleSlides =
        elRef.current?.querySelectorAll(".swiper-slide-visible") ?? [];
      let allLoaded = true;
      let hasMedia = false;

      visibleSlides.forEach((slide) => {
        const mediaElements = slide.querySelectorAll("img, video");

        if (mediaElements.length > 0) hasMedia = true;

        mediaElements.forEach((el) => {
          if (el instanceof HTMLImageElement) {
            if (!el.complete || el.naturalWidth === 0) {
              allLoaded = false;
            }
          } else if (el instanceof HTMLVideoElement) {
            if (el.readyState < 3) {
              allLoaded = false;
            }
          }
        });
      });

      // If no media elements found, consider loaded
      if (!hasMedia) allLoaded = true;

      if (allLoaded) {
        setMediaLoaded(true);
        storiesSliderRef.current?.enable();
      } else {
        // Retry after delay if not loaded
        mediaCheckTimeoutRef.current = setTimeout(checkMediaLoaded, 300);
      }
    }).current;

    useEffect(() => {
      // Initialize stories slider
      storiesSliderRef.current = createStoriesSlider(elRef.current, {
        Swiper: swiper,
        EffectCube: effectCube,
        allowTouchMove: allowTouchMove,
        enabled: false,
        autoplayDuration,
        onSlidesIndexesChange,
        onAutoplayStart,
        onAutoplayStop,
        close: closeModal,
        storiesSeen: storiesSeen,
      });

      // Initial disable
      setTimeout(() => {
        storiesSliderRef.current?.disable();
      }, 10);

      // Setup media load listeners
      const setupMediaListeners = () => {
        const mediaElements =
          elRef.current?.querySelectorAll("img, video") || [];

        mediaElements.forEach((el) => {
          const handler = () => checkMediaLoaded();

          if (el.tagName === "IMG") {
            el.addEventListener("load", handler);
            el.addEventListener("error", handler);
          } else if (el.tagName === "VIDEO") {
            el.addEventListener("loadeddata", handler);
            el.addEventListener("canplay", handler);
            el.addEventListener("error", handler);
          }
        });
      };

      // Initial check with delay to ensure DOM is ready
      const initialCheckTimeout = setTimeout(() => {
        setupMediaListeners();
        checkMediaLoaded();
      }, 100);

      if (onStoriesSlider) {
        onStoriesSlider(storiesSliderRef.current);
      }

      return () => {
        clearTimeout(initialCheckTimeout);
        if (mediaCheckTimeoutRef.current) {
          clearTimeout(mediaCheckTimeoutRef.current);
        }

        if (storiesSliderRef.current?.destroy) {
          storiesSliderRef.current.destroy();
        }
      };
    }, []);

    return (
      <div className="pt-safe-top pb-safe-bottom">
        <div ref={elRef} className="stories-slider">
          <div className="swiper">
            {!mediaLoaded && <StorySpinner />}
            <div className="swiper-wrapper">{children}</div>
          </div>
        </div>
      </div>
    );
  }
);

export default StoriesSlider;
